// EU 학습데이터 요약 공개 문서 관리 — 05-admin §4.5 (AI Act 53조(1)(d)).
// GET/PUT /v1/admin/training-summary + POST /v1/admin/training-summary/publish (01 §3.M, admin).
import { api, errMsg, isNotReady } from "../core/api.js";
import { h, mount, notReadyState } from "../core/dom.js";
import type { TrainingSummary } from "../models.js";

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("ko-KR");
}

export async function summaryView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const container = h("div", { class: "stack" });
  mount(root, container);
  container.replaceChildren(
    h("h1", {}, "EU 학습데이터 요약 관리"),
    h("div", { class: "state muted" }, "요약 불러오는 중…"),
  );

  let summary: TrainingSummary = {};
  try {
    summary = await api.get<TrainingSummary>("/v1/admin/training-summary");
  } catch (e) {
    container.replaceChildren(
      h("h1", {}, "EU 학습데이터 요약 관리"),
      isNotReady(e)
        ? notReadyState("학습데이터 요약 (/v1/admin/training-summary) 이 아직 구현되지 않았습니다.")
        : h("div", { class: "state error" }, `불러오기 실패: ${errMsg(e)}`),
    );
    return;
  }

  const status = h("p", { class: "small muted" }, "");
  const editor = h("textarea", {
    class: "field",
    rows: "16",
    placeholder: "AI Act 53조(1)(d) 학습 콘텐츠 요약 — 데이터 출처/카테고리/opt-out 반영 요약",
  }) as HTMLTextAreaElement;
  editor.value = summary.content ?? "";

  async function saveDraft(): Promise<void> {
    status.textContent = "저장 중…";
    status.className = "small muted";
    try {
      await api.put("/v1/admin/training-summary", { content: editor.value });
      status.textContent = "초안 저장됨 (감사 로그 기록). 공개하려면 발행하세요.";
      status.className = "small ok";
    } catch (e) {
      status.textContent = isNotReady(e) ? "엔드포인트 준비중 (PUT 미구현)." : `저장 실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  async function publish(): Promise<void> {
    if (!window.confirm("현재 요약본을 공개 발행할까요? (공개 URL로 게시 — 감사 로그 기록)")) return;
    status.textContent = "발행 중…";
    status.className = "small muted";
    try {
      // 발행 전 최신 초안 저장 보장.
      await api.put("/v1/admin/training-summary", { content: editor.value });
      const res = await api.post<TrainingSummary>("/v1/admin/training-summary/publish", {});
      summary = { ...summary, ...res, published: true };
      status.textContent = "발행되었습니다.";
      status.className = "small ok";
      render();
    } catch (e) {
      status.textContent = isNotReady(e) ? "엔드포인트 준비중 (publish 미구현)." : `발행 실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  function render(): void {
    container.replaceChildren(
      h("h1", {}, "EU 학습데이터 요약 관리"),
      h(
        "p",
        { class: "muted" },
        "AI Act 53조 학습 콘텐츠 요약 공개 문서의 작성·버전 관리·게시 (05-admin §4.5). admin 전용 편집, 변경·발행은 감사 로그.",
      ),
      h(
        "section",
        { class: "card" },
        h("h2", {}, "현재 공개 상태"),
        h(
          "dl",
          { class: "kv" },
          h("dt", {}, "버전"),
          h("dd", {}, summary.version != null ? String(summary.version) : "—"),
          h("dt", {}, "발행 상태"),
          h(
            "dd",
            {},
            h("span", { class: `badge ${summary.published ? "ok" : "warn"}` }, summary.published ? "공개됨" : "미발행"),
          ),
          h("dt", {}, "공개 URL"),
          h(
            "dd",
            {},
            summary.published_url
              ? h("a", { href: summary.published_url, target: "_blank", rel: "noreferrer" }, summary.published_url)
              : "—",
          ),
          h("dt", {}, "최종 발행"),
          h("dd", { class: "muted small" }, summary.published_at ? fmtTime(summary.published_at) : "—"),
          h("dt", {}, "최종 수정"),
          h("dd", { class: "muted small" }, summary.updated_at ? fmtTime(summary.updated_at) : "—"),
        ),
      ),
      h(
        "section",
        { class: "card" },
        h("h2", {}, "요약본 편집 (admin)"),
        editor,
        h(
          "div",
          { class: "row wrap" },
          h("button", { class: "btn", onclick: () => void saveDraft() }, "초안 저장 (PUT)"),
          h("button", { class: "btn danger", onclick: () => void publish() }, "공개 발행"),
        ),
        status,
      ),
    );
  }

  render();
}
